import React from "react";
import { useHomeLayoutConfig } from "@/modules/homeDisplay/hooks";
import "./SgSpinner.scss";

export type SgSpinnerName =
  | "bubbles"
  | "circles"
  | "crescent"
  | "dots"
  | "lines";
export const SG_SPINNER_NAMES: readonly SgSpinnerName[] = [
  "bubbles",
  "circles",
  "crescent",
  "dots",
  "lines",
];

export interface SgSpinnerProps {
  /** 不传 → 后台 homeLayout.loadingSpinner → 皮肤缺省 bubbles */
  name?: SgSpinnerName;
  /** 盒尺寸（px 或 css 长度），缺省 28px（Ionic 默认） */
  size?: number | string;
  /** 颜色，缺省金 #fbc63d（竞品 ion-loading --spinner-color） */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

/** 读后台配置的 loading 形态（空 / 非法 → bubbles） */
export function useSgSpinnerName(override?: SgSpinnerName): SgSpinnerName {
  const cfg = useHomeLayoutConfig()?.loadingSpinner;
  if (override) return override;
  return SG_SPINNER_NAMES.includes(cfg as SgSpinnerName)
    ? (cfg as SgSpinnerName)
    : "bubbles";
}

const CIRCLE = (count: number, dur: number) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (2 * Math.PI * i) / count;
    return {
      top: 32 * Math.sin(angle),
      left: 32 * Math.cos(angle),
      delay: (dur * i) / count - dur,
    };
  });
const BUBBLES = CIRCLE(9, 1000);
const CIRCLES = CIRCLE(8, 1000);
const LINES = Array.from({ length: 8 }, (_, i) => ({
  rotate: (360 / 8) * i + (i < 4 ? 180 : -180),
  delay: (1000 * i) / 8 - 1000,
}));

/**
 * Ionic ion-spinner 复刻（md 各形态的 svg / 时长 / 相位与 ionic core 的 spinner 配置一致），
 * 模板内所有 loading 统一走它：形态可由后台 homeLayout.loadingSpinner 切换。
 */
export const SgSpinner: React.FC<SgSpinnerProps> = ({
  name,
  size = 28,
  color = "#fbc63d",
  className = "",
  style,
}) => {
  const kind = useSgSpinnerName(name);
  const box: React.CSSProperties = {
    width: size,
    height: size,
    color,
    ...style,
  };
  return (
    <span
      className={`sg-spinner sg-spinner--${kind} ${className}`.trim()}
      style={box}
      role="progressbar"
      aria-hidden="true"
    >
      {kind === "bubbles" &&
        BUBBLES.map((d, i) => (
          <svg
            key={i}
            viewBox="0 0 64 64"
            style={{
              top: `${d.top}%`,
              left: `${d.left}%`,
              animationDelay: `${d.delay}ms`,
            }}
          >
            <circle transform="translate(32,32)" r="5" />
          </svg>
        ))}
      {kind === "circles" &&
        CIRCLES.map((d, i) => (
          <svg
            key={i}
            viewBox="0 0 64 64"
            style={{
              top: `${d.top}%`,
              left: `${d.left}%`,
              animationDelay: `${d.delay}ms`,
            }}
          >
            <circle transform="translate(32,32)" r="5" />
          </svg>
        ))}
      {kind === "crescent" && (
        <svg viewBox="0 0 64 64">
          <circle transform="translate(32,32)" r="26" />
        </svg>
      )}
      {kind === "dots" &&
        [0, 1, 2].map(i => (
          <svg
            key={i}
            viewBox="0 0 64 64"
            style={{ left: `${32 - 32 * i}%`, animationDelay: `${-110 * i}ms` }}
          >
            <circle transform="translate(32,32)" r="6" />
          </svg>
        ))}
      {kind === "lines" &&
        LINES.map((d, i) => (
          <svg
            key={i}
            viewBox="0 0 64 64"
            style={{
              transform: `rotate(${d.rotate}deg)`,
              animationDelay: `${d.delay}ms`,
            }}
          >
            <line transform="translate(32,32)" y1="14" y2="26" />
          </svg>
        ))}
    </span>
  );
};

export default SgSpinner;
